import styled from '@emotion/styled';
import BackLink from '@ep/components/BackLink';
import Button from '@ep/components/Button';
import Container from '@ep/components/Container';
import Link from '@ep/components/Link';
import Media from '@ep/components/Media';
import ModalDialog from '@ep/components/ModalDialog';
import SocialShare from '@ep/components/SocialShare';
import { H1Large, H4, SecondaryText, Subhead } from '@ep/components/Typography';
import theme from '@ep/theme';
import { Gcms_Post } from '@ep/types/gatsbyTypes';
import getHtmlProp from '@ep/utils/getHtmlProp';
import scrollToJustAbove from '@ep/utils/scrollToJustAbove';
import wordLibrary from '@ep/utils/topicsAndTags';
import { DateTime } from 'luxon';
import React, { useEffect, useState } from 'react';
import {
  BackToTop,
  MenuContainer,
  MenuLink,
  StyledArrow,
} from '../VersionedDocumentModule';
import urlHasFirstSegment from '@ep/utils/urlHasSegment';

export interface PostModuleProps {
  post: Gcms_Post;
  refferer: string;
}

interface FeaturedImageProps {
  post: Gcms_Post;
}

const FeaturedImage = styled(Media)<FeaturedImageProps>`
  ${({ post }) =>
    post.postType === 'Master_Series' ? 'width: 91.5%;' : 'width: 65%;'};
  .vidyard-lightbox-image {
    max-height: inherit;
  }
  img {
    max-width: 1110px;
    max-height: 30rem;
    object-position: 0 0;
  }
  ${theme.mediaQueries.mobileAndTablet} {
    width: 100%;
  }
`;

const StyledContainer = styled(Container)`
  position: relative;
  padding: 6.25rem 0 0;

  ${theme.mediaQueries.tablet} {
    padding: 2rem 0 6.25rem;
  }

  ${theme.mediaQueries.mobile} {
    padding: 2rem 0 5rem;
  }
`;

const Description = styled(Subhead)`
  margin-bottom: 2rem;
`;

const Date = styled(SecondaryText)`
  margin-bottom: 1rem;
  0 ${theme.mediaQueries.mobileAndTablet} {
    margin-bottom: 2rem;
  }
`;

const Author = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
`;

const AuthorName = styled(H4.withComponent(Link))`
  display: block;
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;
  font-size: 1.2rem;
`;

const Writer = styled(H4)`
  display: block;
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;
  font-size: 1.2rem;
`;

const Content = styled.div`
  margin-bottom: 3rem;
  display: flex;
  padding: 3.5rem 0 0;

  ${theme.mediaQueries.mobileAndTablet} {
    flex-direction: column;
  }
`;

const Main = styled.div`
  flex: 2;
  img {
    max-width: 1110px;
    max-height: 30rem;
    object-fit: contain;
    object-position: 0 0;
  }
  a {
    color: ${theme.colors.epLinkBlue};
    &:hover {
      text-decoration: underline;
    }
  }
  .no-decoration {
    pointer-events: none;
    color: ${theme.colors.darkBlue}!important;
    cursor: auto !important;
    &:hover {
      text-decoration: none !important;
      cursor: auto !important;
    }
  }
  ${theme.mediaQueries.mobileAndTablet} {
    img {
      max-width: 100%;
      height: fit-content;
    }
  }
`;

const Topic = styled.div`
  margin-top: 2rem;
  padding: 0 0rem;
  color: ${theme.colors.darkGray};
  font-size: ${theme.typography.toRem(18)};
  line-height: 2rem;
  text-transform: uppercase;
  font-family: ${theme.typography.boldFont};
`;

const Tags = styled.div`
  margin-bottom: 3rem;
  display: flex;
  flex-wrap: wrap;
  a:not(:first-of-type):before {
    display: inline-block;
    content: ',';
  }
`;

const Tag = styled.span`
  margin-left: 0.5rem;
  padding: 0 0rem;
  color: ${theme.colors.darkGray};
  font-size: ${theme.typography.toRem(18)};
  line-height: 2rem;
  text-transform: uppercase;
  font-family: ${theme.typography.boldFont};
`;

const TagImg = styled('img')`
  margin-top: 5px;
  width: 20px;
  height: 20px;
`;

const PodcastImgContainer = styled('div')`
  float: right;
  width: 5rem;
`;

const PodcastImg = styled('img')`
  margin-left: 2rem;
  width: 3rem;
`;

const StyledBackLink = styled(BackLink)`
  margin-bottom: 1rem;
`;

const StyledMenuContainer = styled(MenuContainer)`
  margin-top: 3rem;
`;

const ButtonWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-start;
`;

const StyledButton = styled(Button)`
  outline: none;

  ${theme.mediaQueries.mobileAndTablet} {
    height: 2.5rem;
    font-size: ${theme.typography.toRem(14)};
    padding: 0 1rem;
  }
`;

function PostModule({ post, refferer }: PostModuleProps) {
  const displayDate = DateTime.fromISO(post.displayDate);
  const currentUrl =
    typeof window !== 'undefined' ? window.location.pathname : '';

  let postTopicPretty: string = '';
  if (post.postTopic) {
    postTopicPretty = wordLibrary[post.postTopic as keyof typeof wordLibrary];
  }
  const [open, setOpen] = React.useState(false);
  const [scrollElement, setScrollElement] = useState(null);

  useEffect(() => {
    if (document.getElementsByClassName(`section`)) {
      setScrollElement(document.getElementsByClassName(`section`));
    }
  }, []);
  return (
    <>
      <StyledContainer>
        {refferer && urlHasFirstSegment(refferer, 'independents') ? (
          <StyledBackLink label="Independents Home" to={refferer} />
        ) : post.postType === 'Blog' ? (
          <StyledBackLink label="Blog Home" to="/blog/" />
        ) : post.postType === 'Master_Series' ? (
          <StyledBackLink label="Master Series Home" to="/master-series/" />
        ) : post.postType === 'Legal_And_Compliance' ? (
          <StyledBackLink
            label="Legal & Compliance Home"
            to="/legal-and-compliance/"
          />
        ) : post.postType === 'Industry_News' ? (
          <StyledBackLink label="Industry News Home" to="/industry-news/" />
        ) : post.postType === 'UK_Casting' ? (
          <StyledBackLink
            label="UK Casting Community Blog Home"
            to="/uk-casting-community/blog/"
          />
        ) : (
          <StyledBackLink label="Newsroom Home" to="/newsroom/" />
        )}
        <H1Large>{post.title}</H1Large>
        <Description>{post.description}</Description>
        <Date>{displayDate.toLocaleString(DateTime.DATE_FULL)}</Date>
        {post.author &&
          post.postType != 'In_The_News' &&
          post.postType != 'Press_Release' && (
            <>
              <Author>
                <div>
                  <Writer>{post.author.name}</Writer>
                </div>
              </Author>
            </>
          )}
        <SocialShare pathname={`/${post.slug}`} instanceAt={'PostModule'} />
        {post.logoImage ? (
          !post.logoImage.options ||
          (post.logoImage.options &&
            post.logoImage.options.search('clickable') === -1) ? (
            <FeaturedImage media={post.logoImage} post={post} lightbox />
          ) : (
            <FeaturedImage
              media={post.logoImage}
              onClick={() => setOpen(true)}
              style={{ cursor: 'pointer' }}
              post={post}
            />
          )
        ) : !post.logoImage && post.featuredImage ? (
          !post.featuredImage.options ||
          (post.featuredImage.options &&
            post.featuredImage.options.search('clickable') === -1) ? (
            <FeaturedImage media={post.featuredImage} post={post} lightbox />
          ) : (
            <FeaturedImage
              media={post.featuredImage}
              onClick={() => setOpen(true)}
              style={{ cursor: 'pointer' }}
              post={post}
            />
          )
        ) : (
          ''
        )}
        {!post.logoImage && post.featuredImage ? (
          <ModalDialog
            open={open}
            setOpen={setOpen}
            modalDialogMedia={post.featuredImage}
          />
        ) : (
          <ModalDialog
            open={open}
            setOpen={setOpen}
            modalDialogMedia={post.logoImage}
          />
        )}
        <PodcastImgContainer>
          {post.applePodcastsLink && (
            <a target="_blank" href={post.applePodcastsLink}>
              <PodcastImg src="/images/master_series_apple_square.jpg" />
            </a>
          )}
          {post.googlePodcastsLink && (
            <a target="_blank" href={post.googlePodcastsLink}>
              <PodcastImg src="/images/master_series_google_square.jpg" />
            </a>
          )}
          {post.pandoraPodcastsLink && (
            <a target="_blank" href={post.pandoraPodcastsLink}>
              <PodcastImg src="/images/master_series_pandora_square.jpg" />
            </a>
          )}
          {post.spotifyPodcastsLink && (
            <a target="_blank" href={post.spotifyPodcastsLink}>
              <PodcastImg src="/images/master_series_spotify_square.jpg" />
            </a>
          )}
        </PodcastImgContainer>
        {post.sectionNames.length > 0 && (
          <StyledMenuContainer id="postMenu">
            {post.sectionNames.map((title: string, index: number) => {
              return (
                <MenuLink
                  onClick={() => scrollToJustAbove(scrollElement[index], 100)}
                >
                  {title}
                </MenuLink>
              );
            })}
          </StyledMenuContainer>
        )}
        <Content>
          <Main {...getHtmlProp(post.body.html)} />
          {post.sectionNames.length > 0 && (
            <BackToTop
              onClick={() =>
                scrollToJustAbove(document.getElementById('postMenu'), 160)
              }
            >
              <StyledArrow />
              <p>Back to top</p>
            </BackToTop>
          )}
        </Content>
        {post.buttonLink && (
          <ButtonWrapper>
            <StyledButton label={post.buttonLabel} link={post.buttonLink} />
          </ButtonWrapper>
        )}
        {post.postType && post.postTopic && post.postType != 'UK_Casting' && (
          <Topic>
            <>
              <strong>Topic: </strong>
              <a
                href={
                  post.postType === 'In_The_News' ||
                  post.postType === 'Press_Release'
                    ? '/newsroom/?topic=' + post.postTopic
                    : '/' +
                      post.postType.toLowerCase().replace(/_/g, '-') +
                      '/?topic=' +
                      post.postTopic
                }
              >
                {postTopicPretty
                  ? postTopicPretty
                  : post.postTopic.replace(/_/g, ' ')}
              </a>
            </>
          </Topic>
        )}
        {post.postTags.length > 0 && (
          <Tags>
            <TagImg src="/images/tag.png" />
            {post.postTags.map((tag: string) => {
              let postTagPretty = wordLibrary[tag as keyof typeof wordLibrary];
              return (
                <a
                  href={`/search/?tag=${tag.toLowerCase().replace(/_/g, ' ')}`}
                  key={tag}
                >
                  <Tag key={tag}>
                    {postTagPretty ? postTagPretty : tag.replace(/_/g, ' ')}
                  </Tag>
                </a>
              );
            })}
          </Tags>
        )}
      </StyledContainer>
    </>
  );
}

export default PostModule;
